import { formatCurrency, formatCurrencyCompact } from '@/shared/lib/currency';
import { MultiLineChart } from '@/shared/ui/charts';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { dimmed } from '../model/series-config';
import type { RevenueAnalysisSeriesView } from '../model/types';

type RevenueLineChartProps = {
  months: string[];
  series: RevenueAnalysisSeriesView[];
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
};

/** Revenue per resident: one line per series — the values no longer stack. */
export const RevenueLineChart: FC<RevenueLineChartProps> = ({
  months,
  series,
  hoveredKey,
  onHover,
}) => {
  const data: ChartData<'line'> = {
    labels: months.map((m) => dayjs(`${m}-01`).format('MMM YYYY')),
    datasets: series.map((s) => {
      const isMuted = !!hoveredKey && hoveredKey !== s.key;

      return {
        label: s.label,
        data: s.values,
        borderColor: isMuted ? dimmed(s.color) : s.color,
        backgroundColor: 'transparent',
        // Thickening the hovered line as well as dimming the others keeps it
        // findable when several lines cross in the same band.
        borderWidth: hoveredKey === s.key ? 4 : 2,
        pointBackgroundColor: '#fff',
        pointBorderColor: isMuted ? dimmed(s.color) : s.color,
        pointRadius: 3,
      };
    }),
  };

  const options: ChartOptions<'line'> = {
    onHover: (_event, elements) => {
      const index = elements[0]?.datasetIndex;
      onHover(index === undefined ? null : (series[index]?.key ?? null));
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => formatCurrencyCompact(+value) },
      },
    },
    plugins: {
      // MultiLineChart spreads optionsProps over its defaults, so this object
      // *replaces* its whole `plugins` block — both of these have to be restated
      // or the legend comes back and datalabels print raw floats over the lines.
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<'line'>) =>
            `${item.dataset.label}: ${formatCurrency(item.parsed.y ?? 0)}`,
        },
      },
    },
  };

  return <MultiLineChart data={data} optionsProps={options} />;
};
